import React from 'react';
import { Modal, Card, Row, Col, Badge, Button, Table } from 'react-bootstrap';
import { 
  Package, 
  Edit, 
  Trash2, 
  Calendar, 
  DollarSign, 
  BarChart3,
  Tag,
  Hash,
  Layers,
  TrendingUp,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import ProductImage from './ProductImage';

const ProductDetailModal = ({ 
  show, 
  onHide, 
  product, 
  onEdit, 
  onDelete,
  isManager = false 
}) => {
  
  if (!product) return null;

  const {
    id,
    name,
    description,
    price,
    categoryName,
    currentStock,
    stock,
    isActive,
    isLowStock,
    sku,
    barcode,
    imageUrl,
    image_url,
    image,
    costPrice,
    minThreshold,
    maxThreshold,
    createdAt,
    updatedAt
  } = product;

  // Fonctions utilitaires
  const formatPrice = (price) => {
    if (!price) return '0 FCFA';
    return new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price) + ' FCFA';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStockStatus = () => {
    const stockCount = currentStock || stock || 0;
    
    if (stockCount === 0) {
      return { variant: 'danger', text: 'Rupture de stock', icon: AlertTriangle };
    } else if (stockCount <= (minThreshold || 5)) {
      return { variant: 'warning', text: 'Stock faible', icon: AlertTriangle };
    } else {
      return { variant: 'success', text: 'Stock correct', icon: CheckCircle };
    }
  };

  const stockStatus = getStockStatus();
  const StatusIcon = stockStatus.icon;

  // Calculs
  const totalValue = (currentStock || stock || 0) * (costPrice || 0);
  const margin = price && costPrice ? price - costPrice : 0;
  const marginPercent = price && costPrice && price > 0 ? ((margin / price) * 100).toFixed(1) : 0;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <Package size={24} className="me-2 text-primary" />
          Détails du produit
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row>
          {/* Colonne Image et Actions */}
          <Col md={4}>
            <Card className="h-100">
              <Card.Body className="text-center">
                {/* Image principale */}
                <div className="mb-3">
                  {(imageUrl || image_url || image) ? (
                    <ProductImage 
                      product={product} 
                      size="large"
                      style={{
                        width: '100%',
                        maxWidth: '280px',
                        height: '200px',
                        borderRadius: '8px'
                      }}
                      showPlaceholder={true}
                    />
                  ) : (
                    <div 
                      className="bg-light d-flex align-items-center justify-content-center"
                      style={{ height: '200px', borderRadius: '8px' }}
                    >
                      <div>
                        <Package size={48} className="text-muted mb-2" />
                        <p className="text-muted mb-0">Aucune image</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Statut du produit */}
                <div className="mb-3">
                  <Badge bg={isActive ? 'success' : 'secondary'} className="mb-2">
                    {isActive ? (
                      <>
                        <CheckCircle size={14} className="me-1" />
                        Produit actif
                      </>
                    ) : (
                      <>
                        <AlertTriangle size={14} className="me-1" />
                        Produit inactif
                      </>
                    )}
                  </Badge>
                </div>

                {/* Actions manager */}
                {isManager && (
                  <div className="d-grid gap-2">
                    <Button 
                      variant="primary" 
                      onClick={() => onEdit?.(product)}
                      className="d-flex align-items-center justify-content-center"
                    >
                      <Edit size={16} className="me-2" />
                      Modifier
                    </Button>
                    <Button 
                      variant="outline-danger"
                      onClick={() => onDelete?.(product)}
                      className="d-flex align-items-center justify-content-center"
                    >
                      <Trash2 size={16} className="me-2" />
                      Supprimer
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Colonne Informations principales */}
          <Col md={8}>
            <Row>
              {/* Informations de base */}
              <Col md={12}>
                <Card className="mb-3">
                  <Card.Header>
                    <h5 className="mb-0">📋 Informations générales</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <h4 className="text-primary mb-3">{name}</h4>
                        
                        <div className="mb-3">
                          <small className="text-muted d-block">Catégorie</small>
                          <div className="d-flex align-items-center">
                            <Tag size={16} className="me-2 text-muted" />
                            <span>{categoryName || 'Non catégorisé'}</span>
                          </div>
                        </div>

                        {description && (
                          <div className="mb-3">
                            <small className="text-muted d-block">Description</small>
                            <p className="mb-0">{description}</p>
                          </div>
                        )}
                      </Col>

                      <Col md={6}>
                        {sku && (
                          <div className="mb-3">
                            <small className="text-muted d-block">SKU</small>
                            <div className="d-flex align-items-center">
                              <Hash size={16} className="me-2 text-muted" />
                              <code>{sku}</code>
                            </div>
                          </div>
                        )}

                        {barcode && (
                          <div className="mb-3">
                            <small className="text-muted d-block">Code-barres</small>
                            <div className="d-flex align-items-center">
                              <Layers size={16} className="me-2 text-muted" />
                              <code>{barcode}</code>
                            </div>
                          </div>
                        )}

                        <div className="mb-3">
                          <small className="text-muted d-block">Identifiant</small>
                          <code className="small">{id}</code>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              {/* Prix et marges */}
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">
                      <DollarSign size={18} className="me-2" />
                      Prix et marges
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Table size="sm" className="mb-0">
                      <tbody>
                        <tr>
                          <td><strong>Prix de vente :</strong></td>
                          <td className="text-end">
                            <span className="h5 text-primary mb-0">
                              {formatPrice(price)}
                            </span>
                          </td>
                        </tr>
                        {costPrice && (
                          <>
                            <tr>
                              <td>Prix d'achat :</td>
                              <td className="text-end">{formatPrice(costPrice)}</td>
                            </tr>
                            <tr>
                              <td>Marge unitaire :</td>
                              <td className="text-end">
                                <span className={margin >= 0 ? 'text-success' : 'text-danger'}>
                                  {formatPrice(margin)}
                                </span>
                              </td>
                            </tr>
                            <tr>
                              <td>Marge (%) :</td>
                              <td className="text-end">
                                <span className={marginPercent >= 0 ? 'text-success' : 'text-danger'}>
                                  {marginPercent}%
                                </span>
                              </td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>

              {/* Stock */}
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">
                      <BarChart3 size={18} className="me-2" />
                      Stock
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span>Quantité actuelle :</span>
                        <span className="h5 mb-0">{currentStock || stock || 0}</span>
                      </div>
                      <Badge bg={stockStatus.variant} className="w-100 py-2">
                        <StatusIcon size={14} className="me-2" />
                        {stockStatus.text}
                      </Badge>
                    </div>

                    <Table size="sm" className="mb-0">
                      <tbody>
                        {minThreshold && (
                          <tr>
                            <td>Seuil minimum :</td>
                            <td className="text-end">{minThreshold}</td>
                          </tr>
                        )}
                        {maxThreshold && (
                          <tr>
                            <td>Seuil maximum :</td>
                            <td className="text-end">{maxThreshold}</td>
                          </tr>
                        )}
                        {costPrice && (
                          <tr>
                            <td><strong>Valeur totale :</strong></td>
                            <td className="text-end">
                              <strong>{formatPrice(totalValue)}</strong>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>

              {/* Dates */}
              <Col md={12}>
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">
                      <Calendar size={18} className="me-2" />
                      Historique
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <small className="text-muted d-block">Créé le</small>
                        <span>{formatDate(createdAt)}</span>
                      </Col>
                      <Col md={6}>
                        <small className="text-muted d-block">Dernière modification</small>
                        <span>{formatDate(updatedAt)}</span>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fermer
        </Button>
        {isManager && (
          <Button variant="primary" onClick={() => onEdit?.(product)}>
            <Edit size={16} className="me-2" />
            Modifier ce produit
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ProductDetailModal;